import { useCallback, useRef } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { InteractionSource, PointerInteractionSource } from '@/charts/types';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';

// Return an `onUserInteraction` for a d3 chart. It sends `eventName` with `source: 'click'` or `'drag'`, and the
// arrow key steps that follow send `eventName` with `source: 'keyboard'`.
export const useChartInteractionTracking = (
    eventName: string,
    properties: Record<string, unknown>
): ((source: PointerInteractionSource) => void) => {
    const mixpanel = useMixpanel();
    const { setKeyboardInteractionHandler } = useTimelineNavigationContext();
    const latest = useRef({ mixpanel, eventName, properties });
    latest.current = { mixpanel, eventName, properties };

    const track = useCallback((source: InteractionSource) => {
        const { mixpanel, eventName, properties } = latest.current;
        mixpanel?.track(eventName, { ...properties, source });
    }, []);

    return useCallback(
        (source: PointerInteractionSource) => {
            track(source);
            setKeyboardInteractionHandler(track);
        },
        [track, setKeyboardInteractionHandler]
    );
};
